import React, { useRef, useState, useEffect, useCallback } from 'react'

const SuggestedPrompts = ({
  label = 'What is it?',
  onClick,
  disabled = false,
  className = '',
  animationDelay = 0,
  ...props
}) => {
  const handleClick = () => {
    if (disabled) return;
    if (onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`
        inline-flex
        items-center
        gap-v3-xs
        px-v3-sm
        py-v3-xs
        rounded-v3-xl
        font-normal
        bg-[var(--dt-color-surface-100)]
        border-v3-black-inactive
        text-[var(--dt-color-element-100)]
        border
        transition-all
        duration-200
        font-inter-variable
        max-w-full
        disabled:opacity-50
        disabled:cursor-not-allowed
        disabled:hover:border-current
        disabled:hover:text-current
        hover:!bg-transparent
        hover:text-[var(--dt-color-brand-100)]
        hover:border-[var(--dt-color-brand-100)]
        animate-fadeInUp
        ${className}
      `}
      style={{
        animationDelay: `${animationDelay}ms`,
        animationFillMode: 'both'
      }}
      {...props}
    >
      <span className="text-left break-words">
        {label}
      </span>
    </button>
  )
}

// Enhanced container component for suggested prompts with drag scrolling and localStorage management
const SuggestedPromptsContainer = ({
  prompts = [],
  onPromptClick,
  kb_id,
  disabled = false,
  className = '',
  showOncePerKbId = false,
  enableDragScroll = true,     // kept for backward compat if used standalone
  enableHoverScroll = true,
  useParentScroller = false,   // NEW: tells us to not render our own scroll div
  onLastPromptHover,           // NEW: parent callback to scroll-to-end
  enableAnimation = true,
  animationDelay = 100,       
  ...props
}) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [shouldShow, setShouldShow] = useState(true);
  const hoverTimeoutRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // Cleanup timeout and interval on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  // Check localStorage for kb_id specific visibility
  useEffect(() => {
    if (showOncePerKbId && kb_id) {
      const storageKey = `suggested_prompts_shown_${kb_id}`;
      const hasShown = localStorage.getItem(storageKey);
      const shouldShowValue = !hasShown;
      setShouldShow(shouldShowValue);
    }
  }, [kb_id, showOncePerKbId]);

  // Mark as shown in localStorage
  const markAsShown = useCallback(() => {
    if (showOncePerKbId && kb_id) {
      const storageKey = `suggested_prompts_shown_${kb_id}`;
      localStorage.setItem(storageKey, 'true');
      setShouldShow(false);
    }
  }, [kb_id, showOncePerKbId]);

  // Handle prompt click
  const handlePromptClick = useCallback((prompt, index) => {
    if (disabled) return;

    // Mark as shown when user interacts
    markAsShown();

    if (onPromptClick) {
      onPromptClick(prompt.content, index);
    }
  }, [disabled, markAsShown, onPromptClick]);

  // Drag scrolling functionality
  const handleMouseDown = useCallback((e) => {
    if (!enableDragScroll || !containerRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);

    // Prevent text selection during drag
    e.preventDefault();
  }, [enableDragScroll]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;

    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    containerRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Hover scroll functionality for edge detection (for parent scroller mode)
  const handleParentScrollerMouseMove = useCallback((e) => {
    if (!enableHoverScroll || !useParentScroller || !onLastPromptHover) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const containerWidth = rect.width;

    // Define the right edge trigger zone (last 50px of the suggested prompts area)
    const triggerZoneWidth = 50;
    const triggerZoneStart = containerWidth - triggerZoneWidth;

    if (mouseX >= triggerZoneStart) {
    
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }

      // Set timeout for initial scroll trigger
      hoverTimeoutRef.current = setTimeout(() => {
        // First scroll
        onLastPromptHover();

        
        scrollIntervalRef.current = setInterval(() => {
          onLastPromptHover();
        }, 1200); // Continue scrolling every 1.2 seconds
      }, 400); // 400ms delay before starting scroll
    } else {
      
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }
  }, [enableHoverScroll, useParentScroller, onLastPromptHover]);

  // Hover scroll functionality for edge detection (for standalone mode)
  const handleContainerMouseMove = useCallback((e) => {
    if (!enableHoverScroll || !containerRef.current || useParentScroller) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const containerWidth = rect.width;
    const scrollWidth = container.scrollWidth;

    // Only trigger if there's content to scroll
    if (scrollWidth <= containerWidth) return;

    // Define the right edge trigger zone (last 50px of the container)
    const triggerZoneWidth = 50;
    const triggerZoneStart = containerWidth - triggerZoneWidth;

    // Check if mouse is in the right edge trigger zone
    if (mouseX >= triggerZoneStart) {
      // Clear any existing timeout and interval
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }

      // Function to perform one scroll step
      const performScrollStep = () => {
        if (containerRef.current) {
          const currentScroll = containerRef.current.scrollLeft;
          const maxLeft = containerRef.current.scrollWidth - containerRef.current.clientWidth;

         
          const scrollStep = 180; // Scroll by ~180px (one prompt + gap)

          // Calculate target scroll position
          const targetScroll = Math.min(currentScroll + scrollStep, maxLeft);
          const distance = targetScroll - currentScroll;

          
          if (Math.abs(distance) > 10 && currentScroll < maxLeft) {
           
            const duration = 800; 
            const startTime = performance.now();

            const animateScroll = (currentTime) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);

              // Use easeOutQuad for smooth deceleration
              const easeOutQuad = 1 - (1 - progress) * (1 - progress);
              const newScrollLeft = currentScroll + (distance * easeOutQuad);

              if (containerRef.current) {
                containerRef.current.scrollLeft = newScrollLeft;
              }

              if (progress < 1) {
                requestAnimationFrame(animateScroll);
              }
            };

            requestAnimationFrame(animateScroll);
          }
        }
      };

      // Set timeout for initial scroll trigger
      hoverTimeoutRef.current = setTimeout(() => {
        // First scroll
        performScrollStep();

        // Set up continuous scrolling every 1.2 seconds
        scrollIntervalRef.current = setInterval(() => {
          performScrollStep();
        }, 1200); // Continue scrolling every 1.2 seconds
      }, 400); // 400ms delay before starting scroll
    } else {
      // Clear timeout and interval if mouse leaves the trigger zone
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    }
  }, [enableHoverScroll, useParentScroller]);


  const handlePromptHover = useCallback((isLastPrompt) => {
    if (!enableHoverScroll || !containerRef.current || !isLastPrompt) return;

    // Slow scroll to the end when hovering over the last prompt
    const container = containerRef.current;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    if (scrollWidth > clientWidth) {
      container.scrollTo({
        left: scrollWidth - clientWidth,
        behavior: 'smooth'
      });
    }
  }, [enableHoverScroll]);

  // Don't render if shouldn't show
  if (!shouldShow || !prompts || prompts.length === 0) {
    return null;
  }

  const Wrapper = useParentScroller ? 'div' : 'div';

  return (
    <Wrapper
      {...(!useParentScroller ? {
        ref: containerRef,
        onMouseDown: handleMouseDown,
        onMouseMove: (e) => {
          handleMouseMove(e);
          handleContainerMouseMove(e);
        },
        onMouseUp: handleMouseUp,
        onMouseLeave: (e) => {
          handleMouseLeave(e);
          // Clear hover timeout and interval when leaving container
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        },
        style: {
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        },
        className: `
          flex min-w-0 max-w-full gap-v3-xs overflow-x-auto overflow-y-hidden
          scrollbar-hide cursor-grab select-none
          ${isDragging ? 'cursor-grabbing' : ''} ${className}
        `
      } : {
        className: `flex min-w-0 max-w-full gap-v3-xs ${className}`,
        onMouseMove: handleParentScrollerMouseMove,
        onMouseLeave: () => {
          // Clear timeout and interval when leaving the suggested prompts area
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
          }
        }
      })}
      {...props}
    >
      {prompts.map((prompt, index) => {
        const isLastPrompt = index === prompts.length - 1;
        return (
          <div
            key={index}
            className="flex-shrink-0"
            onMouseEnter={() => {
              // For non-parent scroller, use the old hover behavior
              if (!useParentScroller) {
                handlePromptHover(isLastPrompt);
              }
              // For parent scroller, we'll handle hover detection at the container level
            }}
            onMouseLeave={() => {
              // Mouse leave handling is now done at container level
            }}
          >
            <SuggestedPrompts
              label={typeof prompt === 'string' ? prompt : prompt.content}
              onClick={() => handlePromptClick(prompt, index)}
              disabled={disabled}
              animationDelay={enableAnimation ? index * animationDelay : 0}
            />
          </div>
        );
      })}
    </Wrapper>
  );
};

// Add CSS for hiding scrollbars
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('suggested-prompts-styles')) {
  const style = document.createElement('style');
  style.id = 'suggested-prompts-styles';
  style.textContent = scrollbarHideStyles;
  document.head.appendChild(style);
}

export default SuggestedPrompts;
export { SuggestedPromptsContainer };